import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useV2Translation } from '../i18n';
import { AccountHealthBanners } from './AccountHealthBanners';
import { CommandPalette } from './CommandPalette';
import { MobileTopBar } from './MobileTopBar';
import { Sidebar } from './Sidebar';
import { CloseIcon } from './icons';
import { cx, FOCUS_RING_SURFACE } from './styles';
import { useSeatSession } from '../useSeatSession';
import { TourProvider, useTour } from '../tour/TourContext';
import { TourOverlay } from '../tour/TourOverlay';

/**
 * The v2 layout (DESIGN_PLAN §2.8–2.9, §4.2).
 *
 * DOM order matters and is deliberate:
 *   1. admin impersonation banner — full viewport width above BOTH the sidebar
 *      and the content, never dismissible;
 *   2. account-health banners — inside the content column, above `PageHeader`;
 *   3. page-local banners — inside each page, unchanged.
 *
 * z-order: admin banner 60 · palette 70 · tour 80 · drawer 65 / scrim 64 ·
 * desktop sidebar 50 · mobile top bar 40.
 */

const DRAWER_ID = 'v2-nav-drawer';

/**
 * The onboarding tour wraps the shell so every screen can offer a replay and
 * the overlay can spotlight the sidebar. `TourProvider` needs the router and
 * the auth/entitlement queries, all of which are already above `AppShell`.
 */
export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TourProvider>
    <AppShellInner>{children}</AppShellInner>
    <TourOverlay />
  </TourProvider>
);

const AppShellInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useV2Translation();
  const { isAdminSession } = useAuth();
  const { isSeat, ownerEmail } = useSeatSession();
  const { registerDrawer } = useTour();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Lend the drawer to the tour: below `lg` the nav items only exist inside
  // it, so a "click here" step on a phone has to open it first.
  useEffect(() => {
    registerDrawer({
      open: () => setDrawerOpen(true),
      close: () => setDrawerOpen(false),
    });
    return () => registerDrawer(null);
  }, [registerDrawer]);

  const drawerRef = useRef<HTMLDivElement>(null);
  const hamburgerRestore = useRef<Element | null>(null);

  // ⌘K / Ctrl+K anywhere in the shell.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // The drawer closes on any successful navigation.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Escape closes the drawer; body scroll is locked while it is open.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen]);

  // Focus moves into the drawer on open and back to the hamburger on close.
  useEffect(() => {
    if (drawerOpen) {
      hamburgerRestore.current = document.activeElement;
      const id = window.setTimeout(() => {
        drawerRef.current?.querySelector<HTMLElement>('button, a')?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
    const el = hamburgerRestore.current;
    if (el instanceof HTMLElement) el.focus();
    return undefined;
  }, [drawerOpen]);

  const trapFocus = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = drawerRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const openPalette = useCallback(() => {
    setDrawerOpen(false);
    setPaletteOpen(true);
  }, []);

  // The top banner participates in the flex column, so the fixed sidebar and
  // the sticky mobile bar start below it rather than under it.
  //
  // There is only ever ONE such banner, and the 36px offset arithmetic below
  // depends on that. An admin session and a team member session use different tokens
  // and cannot coexist, but the admin banner wins if they somehow did: a
  // support engineer needs to know they are impersonating more urgently than a
  // team member needs the reminder.
  const showSeatBanner = isSeat && !isAdminSession;
  const hasTopBanner = isAdminSession || showSeatBanner;
  const topOffset = hasTopBanner ? 'top-9' : 'top-0';
  const sidebarHeight = hasTopBanner ? 'h-[calc(100vh-2.25rem)]' : 'h-screen';

  return (
    <div
      data-v2-root="app"
      className="flex min-h-screen flex-col bg-[#0a0a0a] font-sans text-white"
    >
      {/* Height is fixed at 36px (h-9) because the fixed sidebar and the sticky
          mobile bar offset themselves by exactly that much. The label truncates
          rather than wrapping so a long email cannot change it. */}
      {isAdminSession && (
        <div className="sticky top-0 z-[60] flex h-9 flex-shrink-0 items-center justify-center border-b border-yellow-500/30 bg-yellow-500/10 px-4 text-center text-xs font-medium text-yellow-400 sm:text-sm">
          <span className="truncate">
            Admin Session — Viewing as {localStorage.getItem('flashtools_admin_email') || 'user'}
          </span>
        </div>
      )}

      {/* A team member is looking at someone else's inventory and sending someone
          else's tickets. Saying whose, permanently and at the top, is the one
          thing that stops a send going out of the wrong account. Same fixed
          36px as the admin banner — see the offset note above. */}
      {showSeatBanner && (
        <div className="sticky top-0 z-[60] flex h-9 flex-shrink-0 items-center justify-center border-b border-[#3b82f6]/30 bg-[#3b82f6]/10 px-4 text-center text-xs font-medium text-[#93c5fd] sm:text-sm">
          <span className="truncate">
            Working in {ownerEmail || 'the account owner'}&rsquo;s inventory
          </span>
        </div>
      )}

      {/* Desktop rail — permanent at lg+, never collapsible. */}
      <div
        className={cx(
          'fixed left-0 z-50 hidden w-64 border-r border-white/[0.08] lg:block',
          topOffset,
          sidebarHeight,
        )}
      >
        <Sidebar onSearchClick={openPalette} />
      </div>

      {/* Mobile drawer + scrim. */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-[64] bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Only mounted while open: a permanently-present `role="dialog"
          aria-modal="true"` node would sit in the a11y tree on every screen,
          including desktop where there is no drawer at all. */}
      {drawerOpen && (
        <div
          id={DRAWER_ID}
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label={t('app.navigation.mainLabel')}
          onKeyDown={trapFocus}
          className="v2-drawer-enter fixed inset-y-0 left-0 z-[65] w-[280px] border-r border-white/[0.08] lg:hidden"
        >
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label={t('app.navigation.closeMenu')}
            className={cx(
              'absolute right-2 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-lg text-white/50 transition-colors hover:bg-white/[0.06] hover:text-white',
              FOCUS_RING_SURFACE,
            )}
          >
            <CloseIcon className="h-4 w-4" />
          </button>
          <Sidebar onSearchClick={openPalette} onNavigate={() => setDrawerOpen(false)} drawer />
        </div>
      )}

      {/* Content column. */}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <div className={cx('sticky z-40 lg:hidden', topOffset)}>
          <MobileTopBar
            onMenuClick={() => setDrawerOpen((open) => !open)}
            onSearchClick={openPalette}
            menuOpen={drawerOpen}
            drawerId={DRAWER_ID}
          />
        </div>

        <AccountHealthBanners />

        <main className="min-w-0 flex-1">{children}</main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
};
